// src/components/Navigation.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch } from "react-icons/fa";
import SearchBar from "./SearchBar";
import { useCart } from "./CartContext";
import { useProductData } from "../context/ProductDataContext";
import { useAuth } from "../context/AuthContext";

export default function Navigation({ menuOpen, setMenuOpen }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { cartItems, openCart } = useCart();
  const { categories } = useProductData();
  const { user, loading: authLoading } = useAuth();

  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);

  const cartCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const featuredCategories = useMemo(() => {
    const leaves = [];
    const walk = (arr = []) =>
      (arr || []).forEach((c) => {
        if (Array.isArray(c.children) && c.children.length) walk(c.children);
        else leaves.push(c);
      });
    walk(categories);
    return leaves
      .slice()
      .sort((a, b) => {
        const ai = a?.image ? 0 : 1;
        const bi = b?.image ? 0 : 1;
        return ai - bi || (a?.name || "").localeCompare(b?.name || "", "hu", { sensitivity: "base" });
      })
      .slice(0, 8);
  }, [categories]);

  const [activeKey, setActiveKey] = useState(pathname);
  useEffect(() => setActiveKey(pathname), [pathname]);

  const navItems = [
    { key: "/", label: "Kezdőlap", path: "/" },
    { key: "/kategoriak", label: "Termékek", path: "/kategoriak", hasDropdown: true },
    { key: "/funkciok", label: "Okos otthon funkciók", path: "/funkciok" },
    { key: "/builder", label: "Okos otthon építő", path: "/builder" },
    { key: "/okoszisztemak", label: "Ökoszisztémák", path: "/okoszisztemak" },
  ];

  useEffect(() => {
    setSearchOpen(false);
    setProductsOpen(false);
    setMenuOpen?.(false);
    setHoveredKey(null);
  }, [pathname, setMenuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setProductsOpen(false);
        setMenuOpen?.(false);
        setHoveredKey(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setMenuOpen]);

  const go = useCallback(
    (to) => {
      navigate(to);
      setProductsOpen(false);
      setMenuOpen?.(false);
      setHoveredKey(null);
    },
    [navigate, setMenuOpen]
  );

  const dropRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (!dropRef.current) return;
      if (!dropRef.current.contains(e.target)) {
        setProductsOpen(false);
        setHoveredKey(null);
      }
    };
    if (productsOpen) {
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }
  }, [productsOpen]);

  const onDropdownEnter = () => setHoveredKey("/kategoriak");
  const onDropdownLeave = () => {
    setHoveredKey(null);
    setProductsOpen(false);
  };

  const toggleMenu = () => {
    setSearchOpen(false);
    setMenuOpen((v) => !v);
  };

  // helper: route-aktivitás (külön szabály a kezdőlapra)
  const isRouteActive = (itemKey) =>
    itemKey === "/"
      ? activeKey === "/" // csak tényleges kezdőlapon
      : activeKey.startsWith(itemKey);

  return (
    <>
      {/* NAVBAR */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 bg-[#0c1a26]/75 backdrop-blur-md text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div
          className={`border-b border-white/10 transition-shadow ${scrolled ? "shadow-[0_6px_24px_-12px_rgba(0,0,0,0.45)]" : "shadow-none"
            }`}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => go("/")}
              className="flex items-center gap-2"
              aria-label="Solarya – Kezdőlap"
            >
              <motion.img
                src="/img/solarya-icon-transparent.webp"
                alt="Solarya Logo"
                className="h-8 w-8"
                whileHover={{ scale: 1.06 }}
              />
              <span className="hidden sm:block text-sm font-semibold tracking-wide text-[#f5e9cb]">
                Solarya
              </span>
            </button>

            {/* Desktop menu */}
            <div className="hidden md:flex items-center gap-6">
              <ul
                className="relative flex items-center gap-4 text-[#f5e9cb] text-sm font-medium"
                onMouseLeave={() => {
                  if (!productsOpen) setHoveredKey(null);
                }}
              >
                {navItems.map((item) => {
                  const isActiveRoute = isRouteActive(item.key);
                  const isHoverShown =
                    hoveredKey === item.key || (productsOpen && item.key === "/kategoriak");

                  return (
                    <li key={item.key} className="relative">
                      <button
                        onClick={() => go(item.path)}
                        onMouseEnter={() => {
                          setHoveredKey(item.key);
                          // fontos: ha nem a "Termékek" (hasDropdown) elem fölé megyünk, ZÁRJUK a dropdownt
                          setProductsOpen(!!item.hasDropdown);
                        }}
                        className="px-1 py-1 hover:opacity-90 transition relative rounded-md"
                        aria-haspopup={item.hasDropdown ? "menu" : undefined}
                        aria-expanded={item.hasDropdown ? productsOpen : undefined}
                      >
                        {/* Hover pill */}
                        <AnimatePresence>
                          {isHoverShown && (
                            <motion.span
                              layoutId="nav-hover-pill"
                              className="absolute inset-x-[-6px] -inset-y-1 rounded-lg bg-white/10"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ type: "spring", stiffness: 400, damping: 30 }}
                              aria-hidden
                            />
                          )}
                        </AnimatePresence>

                        <span className="relative">
                          {item.label}
                          {isActiveRoute && (
                            <motion.span
                              layoutId="nav-underline"
                              className="absolute -bottom-1 left-0 right-0 h-[2px] bg-blue-400 rounded"
                            />
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}

                {/* Search */}
                <li>
                  <button
                    onClick={() => setSearchOpen((v) => !v)}
                    className="text-white text-[18px] hover:opacity-90 relative rounded-md px-1 py-1"
                    onMouseEnter={() => setHoveredKey(null)}
                    aria-label="Keresés megnyitása"
                  >
                    <FaSearch />
                  </button>
                </li>

                {/* Account */}
                <li>
                  <button
                    onClick={() => go(user ? "/fiok" : "/bejelentkezes")}
                    className="relative text-[#f5e9cb] text-sm font-medium rounded-md px-2 py-1 hover:bg-white/10"
                    onMouseEnter={() => setHoveredKey(null)}
                    aria-label={user ? "Fiókom" : "Bejelentkezés"}
                  >
                    {authLoading ? "…" : user ? "Fiókom" : "Belépés"}
                  </button>
                </li>

                {/* Cart */}
                <li>
                  <button
                    onClick={openCart}
                    className="relative text-white text-xl rounded-md px-1 py-1"
                    onMouseEnter={() => setHoveredKey(null)}
                    aria-label="Kosár megnyitása"
                  >
                    <span className="text-2xl">🛒</span>
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-2 text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                        {cartCount}
                      </span>
                    )}
                  </button>
                </li>
              </ul>
            </div>

            {/* Mobile right icons */}
            <div className="md:hidden flex items-center gap-4">
              <button onClick={() => setSearchOpen((v) => !v)} aria-label="Keresés">
                <FaSearch className="text-xl" />
              </button>
              <button
                onClick={() => go(user ? "/fiok" : "/bejelentkezes")}
                className="text-sm text-[#f5e9cb]"
                aria-label={user ? "Fiókom" : "Bejelentkezés"}
              >
                {user ? "Fiók" : "Belépés"}
              </button>
              <button onClick={openCart} className="relative" aria-label="Kosár megnyitása">
                <span className="text-2xl">🛒</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-2 text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                    {cartCount}
                  </span>
                )}
              </button>
              <button className="text-3xl" aria-label="Menü megnyitása" onClick={toggleMenu}>
                ☰
              </button>
            </div>
          </div>

          {/* Desktop: Products mega dropdown */}
          <AnimatePresence>
            {productsOpen && (
              <>
                <motion.div
                  className="hidden md:block absolute inset-0 top-14 bg-black/0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  aria-hidden
                />
                <motion.div
                  ref={dropRef}
                  className="hidden md:block max-w-7xl mx-auto px-6"
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={onDropdownEnter}
                  onMouseLeave={onDropdownLeave}
                >
                  <div className="mt-2 rounded-2xl border border-white/10 bg-[#0c1a26]/90 backdrop-blur-xl text-white shadow-xl overflow-hidden">
                    <div className="grid grid-cols-12 gap-6 p-6">
                      {/* Categories grid */}
                      <div className="col-span-12 lg:col-span-8">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {featuredCategories.map((c) => (
                            <motion.button
                              key={c.id}
                              onClick={() => go(`/kategoriak/${c.id}`)}
                              className="group bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 p-3 text-left"
                              whileHover={{ y: -2 }}
                            >
                              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-white/10 mb-2">
                                <img
                                  src={c.image || "/img/placeholder.webp"}
                                  alt={c.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  loading="lazy"
                                />
                              </div>
                              <div className="text-sm font-semibold text-[#f5e9cb]">
                                {c.name}
                              </div>
                              <div className="text-xs text-white/70">Megnézem</div>
                            </motion.button>
                          ))}
                        </div>
                      </div>

                      {/* Quick links */}
                      <div className="col-span-12 lg:col-span-4">
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4 h-full">
                          <div className="text-sm font-semibold text-[#f5e9cb] mb-3">
                            Gyors elérés
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <button className="text-left px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => go("/kategoriak?all=1")}>
                              Összes termék
                            </button>
                            <button className="text-left px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => go("/funkciok")}>
                              Smart-home funkciók
                            </button>
                            <button className="text-left px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => go("/builder")}>
                              Okosotthon Builder
                            </button>
                            <button className="text-left px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => go("/okoszisztemak")}>
                              Ökoszisztéma áttekintő
                            </button>
                          </div>
                          <div className="mt-4 text-xs text-white/70">
                            Tipp: a funkciók oldalról egy kattintással beállíthatod a releváns szűrőket.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Desktop search drop */}
          <AnimatePresence>
            {searchOpen && (
              <motion.div
                className="hidden md:block bg-[#0c1a26]/95 backdrop-blur-xl border-t border-white/10"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="max-w-7xl mx-auto px-6 py-4">
                  <div className="mx-auto" style={{ width: 460 }}>
                    <SearchBar />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

      {/* MOBILE OVERLAY + DRAWER (nincs kereső a drawerben) */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="md:hidden fixed inset-0 bg-black/50 z-[100]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              className="md:hidden fixed top-0 left-0 bottom-0 w-[84%] max-w-[360px] bg-[#0c1a26] text-white shadow-2xl border-r border-white/10 z-[110] flex flex-col"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
            >
              <div className="h-14 px-4 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-2">
                  <img src="/img/solarya.webp" alt="" className="h-8 w-8" />
                  <span className="text-sm font-semibold text-[#f5e9cb]">Solarya</span>
                </div>
                <button className="text-2xl" onClick={() => setMenuOpen(false)} aria-label="Bezárás">
                  ✕
                </button>
              </div>

              <div className="p-4 grow overflow-auto">
                <ul className="space-y-1">
                  {navItems.map((item) => (
                    <li key={item.key}>
                      <button
                        onClick={() => go(item.path)}
                        className={`w-full text-left px-4 py-3 rounded-lg hover:bg-white/10 ${isRouteActive(item.key) ? "bg-white/10" : ""
                          }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={() => go(user ? "/fiok" : "/bejelentkezes")}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-white/10"
                    >
                      {user ? "Fiókom" : "Bejelentkezés"}
                    </button>
                  </li>
                  {!user && (
                    <li>
                      <button
                        onClick={() => go("/regisztracio")}
                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-white/10"
                      >
                        Regisztráció
                      </button>
                    </li>
                  )}
                </ul>

                {featuredCategories.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-white/70 mb-2">Kiemelt kategóriák</div>
                    <div className="grid grid-cols-2 gap-2">
                      {featuredCategories.slice(0, 6).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => go(`/kategoriak/${c.id}`)}
                          className="bg-white/5 rounded-lg border border-white/10 p-2 text-left"
                        >
                          <div className="aspect-[4/3] rounded overflow-hidden bg-white/10 mb-1">
                            <img
                              src={c.image || "/img/placeholder.webp"}
                              alt={c.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="text-xs">{c.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Mobile search drop (külön overlayből) */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="md:hidden fixed top-14 left-0 right-0 bg-[#0c1a26]/95 backdrop-blur-xl border-t border-white/10 z-[60]"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 py-3">
              <SearchBar />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
